/* eslint-disable react/jsx-key */
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useQueryParams from 'hooks/useQueryParams';

import {
  Page,
  Card,
  Filters,
  ChoiceList,
  Select,
  Tabs,
  TextStyle,
  Stack,
  ResourceList,
  FormLayout,
} from '@shopify/polaris';
import DatePicker from 'react-datepicker';

import Pagination from 'components/Pagination';
import EmptyTable from 'components/EmptyTable';

import { fetchTrades } from 'redux/trades/actions';
import { fetchAdProducts } from 'redux/ads/actions';
import TradeItem from './components/TradeItem';
import TradeItemHeader from './components/TradeItemHeader';
import useDateFilter from 'hooks/useDateFilter';
import { filterPeriods, periodDatesAndLabels } from 'utils/period';
import { formateDateToISOWithoutSSSZ, getStartOfDays } from 'utils/date';
import useUpdateEffect from 'hooks/useUpdatedEffect';
import {
  getValueOfQueryStringKeyFromURL,
  isInitialQuerySetWithForValue,
} from 'utils/helpers';

function Trades({ location }) {
  const dispatch = useDispatch();

  const { data, fetching, meta } = useSelector((state) => state.trades);
  const { products } = useSelector((state) => state.ads);

  const intialQueryParams = useMemo(() => {
    const queryParamsHasForSet = isInitialQuerySetWithForValue();

    return {
      side: '',
      status: getValueOfQueryStringKeyFromURL('status') || '',
      asset: '',
      country: '',
      page: 1,
      order: 'date_created_asc',
      q: '',
      period: 'range',
      from: queryParamsHasForSet
        ? ''
        : formateDateToISOWithoutSSSZ(getStartOfDays(90)),
      to: queryParamsHasForSet ? '' : formateDateToISOWithoutSSSZ(new Date()),
    };
  }, []);

  const { queryParams, updateQueryParams, clearAllFilters } =
    useQueryParams(intialQueryParams);

  const tabs = [
    {
      id: '',
      content: 'All',
    },
    {
      id: 'pending',
      content: 'Pending',
    },
    {
      id: 'processing',
      content: 'Processing',
    },
    {
      id: 'completed',
      content: 'Completed',
    },
    {
      id: 'disputed',
      content: 'Disputed',
    },
    {
      id: 'canceled',
      content: 'Cancelled',
    },
    {
      id: 'expired',
      content: 'Expired',
    },
  ];

  const [selectedTabIndex, setSelectedTabIndex] = React.useState(() => {
    const initialStatus = getValueOfQueryStringKeyFromURL('status') || '';
    const indexOfInitialStatus = tabs.findIndex(
      ({ id }) => id === initialStatus,
    );
    return indexOfInitialStatus !== -1 ? indexOfInitialStatus : 0;
  });

  const {
    handleChangeInSelectedPeriod,
    selectedPeriod,
    selectedDates,
    setSelectedDates,
  } = useDateFilter();

  function handlePreviousPage() {
    updateQueryParams('page', meta.page - 1);
  }

  function handleNextPage() {
    updateQueryParams('page', meta.page + 1);
  }

  const filters = [
    {
      key: 'asset',
      label: 'Asset',
      filter: (
        <ChoiceList
          title="Asset"
          titleHidden
          choices={products.map((product) => ({
            label: `${product.name}`,
            value: `${product.code}`,
          }))}
          selected={queryParams?.asset}
          onChange={(value) => updateQueryParams('asset', value)}
        />
      ),
      shortcut: true,
    },
    {
      key: 'date',
      label: 'Date',
      shortcut: true,
      filter: (
        <FormLayout>
          <Select
            options={filterPeriods}
            label="Date range"
            onChange={handleChangeInSelectedPeriod}
            value={selectedPeriod}
          />
          {selectedPeriod === 'custom-range' && (
            <FormLayout.Group>
              <div>
                <label htmlFor="date_of_birth">Starting</label>
                <DatePicker
                  selected={selectedDates.from}
                  onChange={(date) =>
                    setSelectedDates((dates) => ({ ...dates, from: date }))
                  }
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  dateFormat="dd-MM-yyyy"
                />
              </div>
              <div>
                <label htmlFor="date_of_birth">Ending</label>
                <DatePicker
                  selected={selectedDates.to}
                  onChange={(date) =>
                    setSelectedDates((dates) => ({ ...dates, to: date }))
                  }
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  dateFormat="dd-MM-yyyy"
                />
              </div>
            </FormLayout.Group>
          )}
        </FormLayout>
      ),
    },
  ];

  const orderOptions = [
    {
      label: 'Newest',
      value: 'date_created_asc',
    },
    {
      label: 'Oldest',
      value: 'date_created_desc',
    },
  ];

  const appliedFilters = [];

  if (queryParams.asset) {
    appliedFilters.push({
      key: 'asset',
      label: `Asset ${queryParams.asset}`,
      onRemove: () => updateQueryParams('asset', ''),
    });
  }

  React.useEffect(() => {
    if (location.search) {
      dispatch(fetchTrades(location.search));
    }
  }, [location.search, dispatch]);

  React.useEffect(() => {
    dispatch(fetchAdProducts());
  }, [dispatch]);

  useUpdateEffect(() => {
    if (selectedDates.to !== selectedDates.from) {
      updateQueryParams({
        from: formateDateToISOWithoutSSSZ(selectedDates?.from),
        to: formateDateToISOWithoutSSSZ(selectedDates?.to),
        period: 'range',
        for: '',
      });
    }
  }, [selectedDates]);

  useUpdateEffect(() => {
    switch (selectedPeriod) {
      case 'today':
      case 'yesterday':
        updateQueryParams({
          from: '',
          to: '',
          period: 'day',
          for: formateDateToISOWithoutSSSZ(
            periodDatesAndLabels[selectedPeriod]?.getStartAndEndDates().from,
          ),
        });
        break;
      case 'last-7-days':
      case 'last-30-days':
      case 'last-90-days':
        updateQueryParams({
          period: 'range',
          for: '',
        });

        break;

      default:
        break;
    }
  }, [selectedPeriod]);

  return (
    <Page title="Trades" fullWidth>
      <Card>
        <Tabs
          tabs={tabs}
          selected={selectedTabIndex}
          onSelect={(index) => {
            setSelectedTabIndex(index);
            updateQueryParams('status', tabs[index].id);
          }}
        />
        <Card.Section>
          <Filters
            queryValue={queryParams.q}
            filters={filters}
            appliedFilters={appliedFilters}
            onQueryChange={(value) => updateQueryParams('q', value)}
            onQueryClear={() => updateQueryParams('q', '')}
            onClearAll={clearAllFilters}
          />
        </Card.Section>
        <Card.Section>
          <Stack alignment="center" distribution="equalSpacing">
            <TextStyle>
              {fetching
                ? 'Loading trades'
                : `Showing ${meta?.current_entries_size || ''} trades(s)`}
            </TextStyle>
            <Select
              label="Sort by"
              labelInline
              value={queryParams.order}
              onChange={(value) => updateQueryParams('order', value)}
              options={orderOptions}
            />
          </Stack>
        </Card.Section>

        <React.Fragment>
          <TradeItemHeader />
          <ResourceList
            loading={fetching}
            items={Object.values(data)}
            renderItem={(item) => <TradeItem {...item} />}
            emptyState={<EmptyTable resourceName="Trade" />}
          />
        </React.Fragment>
        <Pagination
          resource={Object.values(data)}
          meta={meta}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
        />
      </Card>
    </Page>
  );
}

export default Trades;
